import { FunctionComponent, RefObject, useRef, useState } from 'react'
import { DateTimePicker, renderTimeViewClock, TimeView } from '@mui/x-date-pickers'
import { format, isAfter, isBefore, isValid, parse, setHours, setMinutes, setSeconds } from 'date-fns'
import { Accessibility } from '@/types/Daycare'
import { DateOrTimeViewWithMeridiem } from '@mui/x-date-pickers/internals'
import styles from './dateTimePicker.module.scss'

interface DateTimePickerComponentProps {
    label?: string
    value: string
    setValue: (date: string) => void
    disabled?: boolean
    readOnly?: boolean
    disablePast?: boolean
    className?: string
    minuteToDisable?: number
    accessibilities?: Array<Accessibility>
    parent?: boolean
    allowEmpty?: boolean
    minDate?: string
    maxDate?: string
    onChange?: (date: string) => void
    onClear?: () => void
}

const DateTimePickerComponent: FunctionComponent<DateTimePickerComponentProps> = ({
    label,
    value,
    setValue,
    disabled = false,
    readOnly = false,
    disablePast = true,
    className,
    minuteToDisable = 15,
    accessibilities = [],
    parent = false,
    allowEmpty = false,
    minDate,
    maxDate,
    onChange,
    onClear
}: DateTimePickerComponentProps) => {
    const pickerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)

    function handleClear(): void {
        onClear?.()
        queueMicrotask(() => pickerRef.current?.querySelector<HTMLElement>(':focus')?.blur())
    }

    function changeDate(date: Date | null): void {
        if (date && isValid(date)) {
            let hours: number = date.getHours()
            let minutes: number = date.getMinutes()

            if (minutes % minuteToDisable !== 0) {
                minutes -= minutes % minuteToDisable
            }

            const newDate: Date = setSeconds(setMinutes(setHours(date, hours), minutes), 0)

            const formatted: string = format(newDate, 'dd-MM-yyyy HH:mm')
            setValue(formatted)

            if (onChange) {
                onChange(formatted)
            }
        } else if (allowEmpty && !date) {
            setValue('JJ-MM-AAAA HH:mm')
            if (onChange) {
                onChange('JJ-MM-AAAA HH:mm')
            }
        }
    }

    function disableTime(timeValue: Date, clockType: TimeView): boolean {
        if (clockType === 'minutes') {
            if (timeValue.getMinutes() % minuteToDisable !== 0) {
                return true
            }

            const isInAccessibilities: boolean | Accessibility | undefined = accessibilities.length
                ? accessibilities.find((accessibility: Accessibility): boolean => {
                      const accStart: Date = parse(accessibility.start, 'HH:mm', new Date())
                      const accEnd: Date = parse(accessibility.end, 'HH:mm', new Date())
                      const valueTime: Date = parse(format(timeValue, 'HH:mm'), 'HH:mm', new Date())

                      return (
                          (isAfter(valueTime, accStart) || valueTime.getTime() === accStart.getTime()) &&
                          (isBefore(valueTime, accEnd) || valueTime.getTime() === accEnd.getTime())
                      )
                  })
                : true

            if (!isInAccessibilities) {
                return true
            }
        }

        if (clockType === 'hours') {
            const isInAccessibilities: boolean | Accessibility | undefined = accessibilities.length
                ? accessibilities.find((accessibility: Accessibility): boolean => {
                      const accStart: Date = parse(accessibility.start, 'HH', new Date())
                      const accEnd: Date = parse(accessibility.end, 'HH', new Date())
                      const valueTime: Date = parse(format(timeValue, 'HH'), 'HH', new Date())

                      return (
                          (isAfter(valueTime, accStart) || valueTime.getTime() === accStart.getTime()) &&
                          (isBefore(valueTime, accEnd) || valueTime.getTime() === accEnd.getTime())
                      )
                  })
                : true

            if (!isInAccessibilities) {
                return true
            }
        }

        return false
    }

    const parsedValue: Date | null = (() => {
        if (allowEmpty && (value === 'JJ-MM-AAAA HH:mm' || value === '')) {
            return null
        }

        try {
            const parsed: Date = parse(value, 'dd-MM-yyyy HH:mm', new Date())
            return isValid(parsed) ? parsed : null
        } catch {
            return null
        }
    })()

    const parsedMinDate: Date | undefined = (() => {
        if (!minDate || minDate === '') return undefined
        try {
            const parsed: Date = parse(minDate, 'dd-MM-yyyy HH:mm', new Date())
            return isValid(parsed) ? parsed : undefined
        } catch {
            return undefined
        }
    })()

    const parsedMaxDate: Date | undefined = (() => {
        if (!maxDate || maxDate === '') return undefined
        try {
            const parsed: Date = parse(maxDate, 'dd-MM-yyyy HH:mm', new Date())
            return isValid(parsed) ? parsed : undefined
        } catch {
            return undefined
        }
    })()

    const [view, setView] = useState<DateOrTimeViewWithMeridiem>('day')

    return (
        <DateTimePicker
            ref={pickerRef}
            label={label}
            value={parsedValue}
            onViewChange={(newView: DateOrTimeViewWithMeridiem): void => setView(newView)}
            view={view}
            orientation='landscape'
            views={
                minuteToDisable === 60
                    ? ['year', 'month', 'day', 'hours']
                    : ['year', 'month', 'day', 'hours', 'minutes']
            }
            viewRenderers={
                minuteToDisable === 60
                    ? {
                          hours: renderTimeViewClock
                      }
                    : {
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock
                      }
            }
            onChange={changeDate}
            slotProps={{
                field: {
                    clearable: Boolean(onClear) && !disabled && !readOnly,
                    onClear: handleClear
                }
            }}
            ampm={false}
            format='dd/MM/yyyy HH:mm'
            disabled={disabled}
            readOnly={readOnly}
            minDateTime={parsedMinDate}
            maxDateTime={parsedMaxDate}
            disablePast={disablePast}
            shouldDisableTime={disableTime}
            className={`${className} ${parent ? styles.Parent : styles.Daycare}`}
        />
    )
}

export default DateTimePickerComponent
